import { useState } from "react";
import { Mail, Phone, MapPin, Send, Instagram } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sessionTypes = [
  "Wedding",
  "Senior Portraits",
  "Family Session",
  "Boudoir",
  "Branding",
  "Maternity",
  "Couples",
  "Lifestyle Newborn",
  "Other",
];

const ContactSection = ({ isVisible }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    session_type: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSessionTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, session_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.session_type || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! I'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        session_type: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 lg:py-32 bg-[#FDFBF9]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-16 ${
            isVisible ? "animate-fade-in-up" : "opacity-0"
          }`}
        >
          <p className="overline text-[#C8A99C] mb-4">Get In Touch</p>
          <h2 className="font-heading text-4xl lg:text-5xl font-light tracking-tight text-[#2C2A28] mb-6">
            Let's Connect
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="font-body text-[#5C5A56] max-w-2xl mx-auto">
            Ready to capture your special moments? Send me a message and let's start planning your session!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div
            className={`${
              isVisible ? "animate-fade-in-up animation-delay-100" : "opacity-0"
            }`}
          >
            <h3 className="font-heading text-2xl text-[#2C2A28] mb-8">
              Contact Information
            </h3>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#C8A99C]" />
                </div>
                <div>
                  <p className="font-body font-medium text-[#2C2A28]">Location</p>
                  <p className="font-body text-[#5C5A56]">Breaux Bridge & Lafayette, Louisiana</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-[#C8A99C]" />
                </div>
                <div>
                  <p className="font-body font-medium text-[#2C2A28]">Email</p>
                  <a
                    href="mailto:hello@brittbaileyphoto.com"
                    className="font-body text-[#5C5A56] hover:text-[#C8A99C] transition-colors"
                  >
                    hello@brittbaileyphoto.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#F5F2EB] rounded-full flex items-center justify-center flex-shrink-0">
                  <Instagram size={20} className="text-[#C8A99C]" />
                </div>
                <div>
                  <p className="font-body font-medium text-[#2C2A28]">Instagram</p>
                  <a
                    href="https://instagram.com/brittbaileyphoto"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-[#5C5A56] hover:text-[#C8A99C] transition-colors"
                  >
                    @brittbaileyphoto
                  </a>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative h-[400px] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_relaxed-margulis-6/artifacts/5cxw5hxn_IMG_2134.JPG"
                alt="Brittany Bailey at her desk"
                className="w-full h-full object-cover" style={{ objectPosition: "center 40%" }}
              />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#A8B5A3] -z-10" />
            </div>
          </div>

          {/* Contact Form */}
          <div
            className={`${
              isVisible ? "animate-fade-in-up animation-delay-200" : "opacity-0"
            }`}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-body text-sm text-[#5C5A56]">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="form-input bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body text-sm text-[#5C5A56]">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="form-input bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-body text-sm text-[#5C5A56]">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="form-input bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="session_type" className="font-body text-sm text-[#5C5A56]">
                    Session Type *
                  </Label>
                  <Select
                    value={formData.session_type}
                    onValueChange={handleSessionTypeChange}
                  >
                    <SelectTrigger
                      className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                      data-testid="contact-session-type-select"
                    >
                      <SelectValue placeholder="Select a session type" />
                    </SelectTrigger>
                    <SelectContent>
                      {sessionTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="font-body text-sm text-[#5C5A56]">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your vision, preferred dates, locations, and any questions you have..."
                  rows={6}
                  className="form-input bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#2C2A28] text-[#FDFBF9] font-body text-sm tracking-wide hover:bg-[#5C5A56] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-form-submit"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
